import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { delay, switchMap, take, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { OrderService } from 'src/app/core/services/order.service';
import { AccountService } from 'src/app/core/services/account.service';
import { environment } from 'src/environments/environment';
import mapboxgl from 'mapbox-gl';
import kingSlizeLocations from '../../../../environments/kingslize_locations.json';
import * as turf from '@turf/turf';
import { MapRepo } from './map.repo';
import { Order } from '../../models/order';
import { distinctUntilChangedObj } from '../../operators/distinct-until-changed-obj';

@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class MapboxComponent implements OnInit, OnDestroy {
  private readonly MAX_ZOOM: number = 20;
  private readonly MIN_ZOOM: number = 10;
  private readonly destroyed$: Subject<void> = new Subject();

  @Input({ required: true }) viewDidEnter!: Subject<void>;
  @Input({ required: true }) mapId!: string;
  @Input() showZoomButtons: boolean = false;

  map?: mapboxgl.Map;
  branchLocation: number[] = [];
  orderLocations: number[][] = [];
  branchLocationGeoJson: any;
  orderLocationsGeoJson: any;

  constructor(
    private accountService: AccountService,
    private orderService: OrderService,
    private mapRepo: MapRepo,
  ) {
    mapboxgl.accessToken = environment.mapboxKey;
  }

  ngOnInit() {
    this.viewDidEnter.pipe(
      take(1),
      delay(50),
      switchMap(() => this.accountService.getBranch()),
      takeUntil(this.destroyed$),
    ).subscribe((branch) => {
      const branchLocation = kingSlizeLocations.find(kingSlizeLocations => kingSlizeLocations.branchId == branch.id);
      if (!branchLocation) throw Error(`Branchlocation with id ${branch.id} not found`);
      this.branchLocation = [branchLocation.lng, branchLocation.lat];
      this.branchLocationGeoJson = turf.featureCollection([turf.point(this.branchLocation, { "description": `<p style="color: #998542;">KingslizePizza ${branchLocation.city}</p>` })]);
      this.initMap();
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  zoomIn(): void {
    if (this.map) {
      this.map.zoomIn();
    }
  }

  zoomOut(): void {
    if (this.map) {
      this.map.zoomOut();
    }
  }

  private initMap(): void {
    this.map =
      new mapboxgl.Map({
        container: this.mapId,
        style: 'mapbox://styles/oussie00/cl4frvada000s15mowbddo7ej?optimize=true',
        center: [this.branchLocation[0], this.branchLocation[1]],
        zoom: 12,
        trackResize: false
      });

    this.map.on('load', () => {
      this.setZoomLevels();
      this.addBranchLocation();
      this.orderService.orders$
        .pipe(
          distinctUntilChangedObj(),
          takeUntil(this.destroyed$)
        )
        .subscribe(orders => {
          this.orderLocations = orders.map(order => [parseFloat(order.client.lng), parseFloat(order.client.lat)]);
          this.orderLocationsGeoJson = turf.featureCollection(this.orderLocations.map((location, index) => turf.point(location, { "index": index + 1, "description": this.getPopupHtml(index + 1, orders[index]) })));
          this.addOrderLocations();
          this.addRoute();
          const allPoints = [this.branchLocation, ...this.orderLocations];
          const centerOfAllPoints = turf.center(turf.featureCollection(allPoints.map(location => turf.point([location[0], location[1]]))));
          this.map?.flyTo({ center: [centerOfAllPoints.geometry.coordinates[0], centerOfAllPoints.geometry.coordinates[1]], zoom: 12 });
        })
    })
  }

  private addBranchLocation(): void {
    this.map?.addSource('branch-source', { type: 'geojson', data: this.branchLocationGeoJson as any });
    this.addBranchLayers();

    this.map?.on('click', 'branch', (e: any) => {
      const coordinates = e.features[0].geometry.coordinates.slice();
      const description = e.features[0].properties.description;

      while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
        coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
      }

      new mapboxgl.Popup()
        .setLngLat(coordinates)
        .setHTML(description)
        .addTo(this.map!);
    });
  }

  private addOrderLocations(): void {
    if (this.map?.getLayer('order-locations')) {
      const orderSource = this.map.getSource('order-source') as mapboxgl.GeoJSONSource;
      orderSource.setData(this.orderLocationsGeoJson);

    } else {
      this.map?.addSource('order-source', { type: 'geojson', data: this.orderLocationsGeoJson as any });
      this.addOrderLayers();

      this.map?.on('click', 'order-locations', (e: any) => {
        const coordinates = e.features[0].geometry.coordinates.slice();
        const description = e.features[0].properties.description;

        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
          coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }

        new mapboxgl.Popup()
          .setLngLat(coordinates)
          .setHTML(description)
          .addTo(this.map!);
      });
    }
  }

  private addRoute(): void {
    this.accountService.getSelectedVehicle()
      .pipe(
        switchMap((vehicle) => this.mapRepo.getRoute([this.branchLocation, ...this.orderLocations, this.branchLocation], vehicle?.type)),
        takeUntil(this.destroyed$),
      ).subscribe((data => {
        if (this.map?.getLayer('routeline-active')) {
          const routeGeoJSON = turf.featureCollection([
            turf.feature(data.routes[0].geometry)
          ]);
          const source = this.map.getSource('route') as mapboxgl.GeoJSONSource;
          source.setData(routeGeoJSON as any);
        } else {
          const routeGeoJSON = turf.featureCollection([
            turf.feature(data.routes[0].geometry)
          ]);
          this.map?.addSource('route', { type: 'geojson', data: routeGeoJSON as any });
          this.addRouteLine();
          this.addRouteArrows();
        }
      }));
  }

  private addBranchLayers(): void {
    this.map?.addLayer({
      id: 'branch',
      type: 'circle',
      source: 'branch-source',
      paint: {
        'circle-radius': 9,
        'circle-color': '#998542',
        'circle-stroke-color': '#998542',
        'circle-stroke-width': 3
      }
    });

    this.map?.addLayer({
      id: 'branch-symbol',
      type: 'symbol',
      source: 'branch-source',
      layout: {
        'icon-image': 'restaurant-pizza',
        'icon-size': 1
      },
      paint: {
        'text-color': '#998542'
      }
    });
  }

  private addOrderLayers(): void {
    this.map?.addLayer({
      id: 'order-locations',
      type: 'circle',
      source: 'order-source',
      paint: {
        'circle-radius': 9,
        'circle-color': 'white',
        'circle-stroke-color': '#998542',
        'circle-stroke-width': 3
      }
    });

    this.map?.addLayer({
      id: 'orders-symbol',
      type: 'symbol',
      source: 'order-source',
      layout: {
        'text-field': ['get', 'index'],
      },
      paint: {
        'text-color': '#998542'
      }
    });
  }

  private addRouteArrows(): void {
    this.map?.addLayer(
      {
        id: 'routearrows',
        type: 'symbol',
        source: 'route',
        layout: {
          'symbol-placement': 'line',
          'text-field': '▶',
          'text-size': ['interpolate', ['linear'], ['zoom'], 12, 24, 22, 60],
          'symbol-spacing': ['interpolate', ['linear'], ['zoom'], 12, 30, 22, 160],
          'text-keep-upright': false
        },
        paint: {
          'text-color': 'black',
          'text-halo-color': 'hsl(55, 11%, 96%)',
          'text-halo-width': 3
        }
      },
      'waterway-label'
    );
  }

  private addRouteLine(): void {
    this.map?.addLayer(
      {
        id: 'routeline-active',
        type: 'line',
        source: 'route',
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': '#998542',
          'line-width': ['interpolate', ['linear'], ['zoom'], 12, 3, 22, 12]
        }
      },
      'waterway-label'
    );
  }

  private getPopupHtml(orderNumber: number, order: Order): string {
    return `<p style="font-family: Poppins; font-weight: 600; font-size: 11px; color: #998542; margin: 5px;">Order nr. ${orderNumber}</p>
      <p style="font-family: Poppins; font-weight: 400; font-size: 11px; margin: 0 5px;" >${order.client.getAddress()}</p>
    `;
  }

  private setZoomLevels(): void {
    this.map?.setMaxZoom(this.MAX_ZOOM);
    this.map?.setMinZoom(this.MIN_ZOOM);
  }
}
